import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { useCohortsVersion } from "@/components/instructor/cohorts-changed";
import { apiErrorText } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";

type Cohort = { id: string; name: string };
type CohortEvent = {
  id: string;
  cohortId: string;
  cohortName: string;
  title: string;
  startsAt: string;
  endsAt: string | null;
  zoomUrl: string;
};

function toDatetimeLocalValue(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

type Props = { courseId: string };

export function CourseCohortEventsPanel({ courseId }: Props) {
  const t = useTranslations("instructor");
  // C4 (CRS-16): server errors are shown by code, never as the server's English.
  const tRoot = useTranslations();
  const errorText = (body: unknown, fallbackKey: string) =>
    apiErrorText(body, tRoot, `instructor.${fallbackKey}`, { namespace: "instructor.errors" });
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [events, setEvents] = useState<CohortEvent[]>([]);
  const [cohortId, setCohortId] = useState("");
  const [title, setTitle] = useState("");
  const [startsAt, setStartsAt] = useState(() =>
    toDatetimeLocalValue(new Date(Date.now() + 24 * 60 * 60 * 1000)),
  );
  const [endsAt, setEndsAt] = useState("");
  const [zoomUrl, setZoomUrl] = useState("");
  const [posting, setPosting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const cohortsVersion = useCohortsVersion(courseId);

  useEffect(() => {
    void (async () => {
      const [cohortRes, eventRes] = await Promise.all([
        apiFetch(`/v1/instructor/courses/${courseId}/cohorts`),
        apiFetch(`/v1/instructor/courses/${courseId}/cohort-events`),
      ]);
      const cohortJson = await cohortRes.json();
      const eventJson = await eventRes.json();
      if (cohortRes.ok) {
        const rows = cohortJson.data.cohorts as Cohort[];
        setCohorts(rows);
        // A deleted cohort can't stay selected.
        setCohortId((current) =>
          rows.some((row) => row.id === current) ? current : rows[0]?.id || "",
        );
      }
      if (eventRes.ok) {
        setEvents(eventJson.data.events as CohortEvent[]);
      }
    })();
  }, [courseId, reloadToken, cohortsVersion]);

  function beginEdit(row: CohortEvent) {
    setEditingId(row.id);
    setCohortId(row.cohortId);
    setTitle(row.title);
    setStartsAt(toDatetimeLocalValue(new Date(row.startsAt)));
    setEndsAt(row.endsAt ? toDatetimeLocalValue(new Date(row.endsAt)) : "");
    setZoomUrl(row.zoomUrl);
    setError(null);
  }

  function resetForm() {
    setEditingId(null);
    setTitle("");
    setZoomUrl("");
    setEndsAt("");
    setStartsAt(toDatetimeLocalValue(new Date(Date.now() + 24 * 60 * 60 * 1000)));
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setPosting(true);
    setError(null);
    try {
      const startsAtIso = new Date(startsAt).toISOString();
      const endsAtIso = endsAt.trim() ? new Date(endsAt).toISOString() : null;
      if (editingId) {
        const res = await apiFetch(`/v1/instructor/courses/${courseId}/cohort-events`, {
          method: "PATCH",
          body: JSON.stringify({
            eventId: editingId,
            title: title.trim(),
            startsAt: startsAtIso,
            endsAt: endsAtIso,
            zoomUrl: zoomUrl.trim(),
          }),
        });
        const json = await res.json();
        if (!res.ok) {
          setError(errorText(json, "cohortEventUpdateFailed"));
          return;
        }
      } else {
        const res = await apiFetch(`/v1/instructor/courses/${courseId}/cohort-events`, {
          method: "POST",
          body: JSON.stringify({
            cohortId,
            title: title.trim(),
            startsAt: startsAtIso,
            endsAt: endsAtIso,
            zoomUrl: zoomUrl.trim(),
          }),
        });
        const json = await res.json();
        if (!res.ok) {
          setError(errorText(json, "cohortEventCreateFailed"));
          return;
        }
      }
      resetForm();
      setReloadToken((value) => value + 1);
    } finally {
      setPosting(false);
    }
  }

  async function handleDelete(eventId: string) {
    if (!window.confirm(t("cohortEventDeleteConfirm"))) return;
    setDeletingId(eventId);
    setError(null);
    try {
      const res = await apiFetch(`/v1/instructor/courses/${courseId}/cohort-events`, {
        method: "DELETE",
        body: JSON.stringify({ eventId }),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(errorText(json, "cohortEventDeleteFailed"));
        return;
      }
      if (editingId === eventId) resetForm();
      setReloadToken((value) => value + 1);
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <section data-testid="course-cohort-events-panel">
      <h2 className="text-lg font-medium">{t("cohortEventsTitle")}</h2>
      <p className="mt-1 text-sm text-[var(--text-muted)]">{t("cohortEventsHint")}</p>

      <ul className="mt-4 space-y-2" data-testid="course-cohort-events-list">
        {events.map((row) => (
          <li
            key={row.id}
            className="rounded border border-[var(--border)] px-3 py-2 text-sm"
            data-testid={`cohort-event-${row.id}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-medium">{row.title}</p>
                <p className="text-[var(--text-muted)]">
                  {row.cohortName} · {new Date(row.startsAt).toLocaleString()}
                  {row.endsAt ? ` – ${new Date(row.endsAt).toLocaleString()}` : ""}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="text-xs text-[var(--text-heading)] underline"
                  onClick={() => beginEdit(row)}
                  data-testid={`cohort-event-edit-${row.id}`}
                >
                  {t("cohortEventEdit")}
                </button>
                <button
                  type="button"
                  className="text-xs text-red-700 underline disabled:opacity-50"
                  disabled={deletingId === row.id}
                  onClick={() => void handleDelete(row.id)}
                  data-testid={`cohort-event-delete-${row.id}`}
                >
                  {deletingId === row.id ? t("cohortEventDeleting") : t("cohortEventDelete")}
                </button>
              </div>
            </div>
          </li>
        ))}
        {events.length === 0 ? (
          <li className="text-sm text-[var(--text-muted)]">{t("cohortEventsEmpty")}</li>
        ) : null}
      </ul>

      <form onSubmit={(e) => void handleSubmit(e)} className="mt-4 space-y-3">
        <label className="block text-sm">
          {t("cohortEventCohort")}
          <select
            value={cohortId}
            onChange={(e) => setCohortId(e.target.value)}
            className="mt-1 w-full rounded border px-3 py-2 text-sm border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]"
            data-testid="cohort-event-cohort"
            required
            disabled={Boolean(editingId)}
          >
            {cohorts.map((cohort) => (
              <option key={cohort.id} value={cohort.id}>
                {cohort.name}
              </option>
            ))}
          </select>
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t("cohortEventTitlePlaceholder")}
          className="w-full rounded border px-3 py-2 text-sm border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]"
          data-testid="cohort-event-title"
          required
        />
        <label className="block text-sm">
          {t("cohortEventStartsAt")}
          <input
            type="datetime-local"
            step={1}
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            className="mt-1 w-full rounded border px-3 py-2 text-sm border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]"
            data-testid="cohort-event-starts-at"
            required
          />
        </label>
        <label className="block text-sm">
          {t("cohortEventEndsAt")}
          <input
            type="datetime-local"
            step={1}
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
            className="mt-1 w-full rounded border px-3 py-2 text-sm border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]"
            data-testid="cohort-event-ends-at"
          />
        </label>
        <input
          type="url"
          value={zoomUrl}
          onChange={(e) => setZoomUrl(e.target.value)}
          placeholder={t("cohortEventZoomPlaceholder")}
          className="w-full rounded border px-3 py-2 text-sm border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]"
          data-testid="cohort-event-zoom-url"
          required
        />
        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            disabled={posting || !cohortId || !title.trim() || !zoomUrl.trim()}
            className="rounded bg-[var(--ink-12)] px-4 py-2 text-sm text-[var(--ink-1)] disabled:opacity-60"
            data-testid="cohort-event-create"
          >
            {posting
              ? editingId
                ? t("cohortEventUpdating")
                : t("cohortEventCreating")
              : editingId
                ? t("cohortEventUpdate")
                : t("cohortEventCreate")}
          </button>
          {editingId ? (
            <button
              type="button"
              onClick={() => resetForm()}
              className="rounded border border-[var(--border-strong)] px-4 py-2 text-sm"
              data-testid="cohort-event-cancel-edit"
            >
              {t("cohortEventCancelEdit")}
            </button>
          ) : null}
        </div>
      </form>
      {error ? (
        <p className="mt-2 text-sm text-red-600" data-testid="cohort-event-error">
          {error}
        </p>
      ) : null}
    </section>
  );
}
